import { ComponentProps, ReactNode } from 'react';
import { TextField } from 'react-admin';

import { GetB2bRecurringInvestmentResponse } from '@Common/types/bo/b2b/recurring_investments';

import { planTypeChoices } from '@sh4res/bo-common/choices/plans';
import {
  b2bRecurringInvestmentFrequencyChoices,
  b2bRecurringInvestmentInitiatorChoices,
  b2bRecurringInvestmentPayerChoices,
  b2bRecurringInvestmentStatusChoices,
} from '@sh4res/bo-common/choices/recurring_investments';
import { ShowFields, ShowSection, ShowWithNavigation } from '@sh4res/bo-common/components';
import { B2bRecurringInvestmentAllocationsList } from '@sh4res/bo-common/components/B2bRecurringInvestments/B2bRecurringInvestmentAllocationsList';
import { B2bRecurringInvestmentLinkedOperations } from '@sh4res/bo-common/components/B2bRecurringInvestments/B2bRecurringInvestmentLinkedOperations';
import { CurrencyField, IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';

const testId = 'BoExternalB2bRecurringInvestmentsShow';

type B2bRecurringInvestmentsShowProps = Pick<
  ComponentProps<typeof ShowWithNavigation>,
  'titleActions' | 'comments' | 'logs'
> & {
  /** Sections rendered after the linked operations, for app specific content */
  extraSections?: ReactNode;
};

export const B2bRecurringInvestmentsShow = ({
  extraSections,
  ...navigationProps
}: B2bRecurringInvestmentsShowProps) => {
  const resourceBasePath = useResourceBasePath();

  return (
    <ShowWithNavigation actions={false} sx={{ height: '100%' }} {...navigationProps}>
      <ShowSection label="resources.bo_common.b2b.recurring_investments.show.title">
        <ShowFields<GetB2bRecurringInvestmentResponse>
          fields={{
            id: (
              <IdField
                reference={`${resourceBasePath}recurring_investments`}
                data-testid={`${testId}_IdField`}
              />
            ),
            b2b_company_plan_type: (
              <SelectChipField
                choices={planTypeChoices}
                data-testid={`${testId}_CompanyPlanTypeField`}
              />
            ),
            status: (
              <SelectChipField
                choices={b2bRecurringInvestmentStatusChoices}
                data-testid={`${testId}_StatusField`}
              />
            ),
            user_input_amount: (
              <CurrencyField currency="user_currency" testId={`${testId}_UserInputAmountField`} />
            ),
            execution_starting_date: (
              <UTCDateField showTime={false} data-testid={`${testId}_ExecutionStartingDateField`} />
            ),
            execution_frequency: (
              <SelectChipField
                choices={b2bRecurringInvestmentFrequencyChoices}
                data-testid={`${testId}_ExecutionFrequencyField`}
              />
            ),
            next_execution_at: (
              <UTCDateField showTime={false} data-testid={`${testId}_NextExecutionAtField`} />
            ),
            last_executed_at: (
              <UTCDateField showTime emptyText="-" data-testid={`${testId}_LastExecutedAtField`} />
            ),
            payer: (
              <SelectChipField
                choices={b2bRecurringInvestmentPayerChoices}
                data-testid={`${testId}_PayerField`}
              />
            ),
            initiator: (
              <SelectChipField
                choices={b2bRecurringInvestmentInitiatorChoices}
                data-testid={`${testId}_InitiatorField`}
              />
            ),
            deductibility: <TextField emptyText="-" data-testid={`${testId}_DeductibilityField`} />,
            b2b_employee_id: (
              <IdField
                reference={`${resourceBasePath}employees`}
                data-testid={`${testId}_EmployeeIdField`}
              />
            ),
            b2b_contract_id: (
              <IdField
                reference={`${resourceBasePath}contracts`}
                data-testid={`${testId}_ContractIdField`}
              />
            ),
            created_at: <UTCDateField showTime data-testid={`${testId}_CreatedAtField`} />,
            updated_at: <UTCDateField showTime data-testid={`${testId}_UpdatedAtField`} />,
          }}
        />
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.recurring_investments.allocations.title">
        <B2bRecurringInvestmentAllocationsList />
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.recurring_investments.show.linked_operations_title">
        <B2bRecurringInvestmentLinkedOperations />
      </ShowSection>
      {extraSections}
    </ShowWithNavigation>
  );
};
